import { zodResolver } from '@hookform/resolvers/zod'
import { InputFormField } from '@repo/shared-client/components/fields'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  buttonVariants,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Skeleton,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { ClientOnly, createFileRoute, Link } from '@tanstack/react-router'
import LogoFull from '@web/src/components/primatives/logo-full/logo-full'
import { useAuth } from '@web/src/hooks/use-auth'
import { useTurnstileWidgetSize } from '@web/src/hooks/use-turnstile-widget-size'
import { getAppConfig } from '@web/src/lib/env.dynamic.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { CircleCheckIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Turnstile } from 'react-turnstile'
import Cookies from 'universal-cookie'
import { z } from 'zod'
import { forgotPasswordPageHeadFromLoaderData } from './forgot-password.seo'

const authFooterLinkClass = cn(buttonVariants({ variant: 'link', size: 'default' }))

export const Route = createFileRoute('/(public)/(main-layout)/auth/forgot-password')({
  staticData: {
    disableSeasonalTheme: true,
  },
  component: ForgotPassword,
  head: () => forgotPasswordPageHeadFromLoaderData(),
})

function ForgotPassword() {
  const { t } = useTranslation()
  const { forgotPassword } = useAuth()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [token, setToken] = useState('')
  const turnstileSize = useTurnstileWidgetSize()

  const formSchema = z.object({
    email: z.email(t('Please enter a valid email address')),
  })

  type FormValues = z.infer<typeof formSchema>

  const formMethods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  })

  const cookies = new Cookies()
  const isCaptchaDisabled = getAppConfig().CAPTCHA_DISABLE === true

  const emailRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    emailRef.current?.focus()
  }, [])

  const onSubmit: SubmitHandler<FormValues> = async ({ email }) => {
    if (!(isCaptchaDisabled || token)) {
      return toast.error(t('Captcha is required'))
    }

    try {
      if (!isCaptchaDisabled) {
        cookies.set('captcha', token, { path: '/' })
      }

      await forgotPassword.mutateAsync({ email, captchaToken: token })

      if (!isCaptchaDisabled) {
        cookies.remove('captcha', { path: '/' })
      }

      setIsSubmitted(true)
    } catch (err) {
      toast.error((err as Error)?.message ?? t('An error occurred'))
    }
  }

  if (isSubmitted) {
    return (
      <div className="relative z-10 flex justify-center py-14">
        <Card className="mx-4 w-full max-w-md shadow-2xl">
          <CardHeader className="flex flex-col items-center gap-2 text-center">
            <LogoFull size="md" />
          </CardHeader>

          <CardContent className="pt-0">
            <Alert className="border-emerald-500/30 bg-emerald-500/10 text-emerald-950 dark:text-emerald-50">
              <CircleCheckIcon aria-hidden className="text-emerald-600 dark:text-emerald-400" />
              <AlertTitle>{t('Password reset link sent')}</AlertTitle>
              <AlertDescription>
                {t('Please check your inbox and spam folder for the password reset link.')}
              </AlertDescription>
            </Alert>
          </CardContent>

          <CardFooter className="justify-center">
            <Link className={authFooterLinkClass} to="/auth/login">
              {t('Back to Sign In')}
            </Link>
          </CardFooter>
        </Card>
      </div>
    )
  }

  return (
    <div className="relative z-10 flex justify-center py-14">
      <Card className="mx-4 w-full max-w-md shadow-2xl">
        <CardHeader className="flex flex-col items-center gap-2 text-center">
          <LogoFull size="md" />
          <h1 className="mt-2 text-center font-semibold text-lg text-muted-foreground">{t('Reset your password')}</h1>
        </CardHeader>

        <CardContent>
          <Form formMethods={formMethods} onSubmit={onSubmit}>
            <div className="flex flex-col gap-4">
              <InputFormField
                autoComplete="email"
                label={t('Email')}
                name="email"
                placeholder={t('Enter your email')}
                ref={emailRef}
                required
              />

              {!isCaptchaDisabled && (
                <ClientOnly fallback={<Skeleton className="h-[65px] w-full" />}>
                  <div className={cn('flex justify-center', turnstileSize === 'compact' ? 'pb-4' : 'pb-0')}>
                    <Turnstile
                      appearance="always"
                      onVerify={(props) => setToken(props)}
                      sitekey={getAppConfig().CAPTCHA_SITEKEY}
                      size={turnstileSize}
                    />
                  </div>
                </ClientOnly>
              )}

              <Button className="w-full" disabled={!(token || isCaptchaDisabled)} size="lg" type="submit">
                {forgotPassword.isPending ? <Spinner className="mr-2" /> : null}
                {t('Send reset link')}
              </Button>
            </div>
          </Form>
        </CardContent>

        <CardFooter className="justify-center">
          <Link className={authFooterLinkClass} to="/auth/login">
            {t('Back to Sign In')}
          </Link>
        </CardFooter>
      </Card>
    </div>
  )
}
